import React, { useCallback, useState, useEffect } from "react";
import { useAnketa } from "../../context_provider";
import {
  MessageCollection,
  type Hierarchical,
  isQuestion,
  type Link,
  type Item,
  ComplexFactory,
} from "anketa-core";
import {
  EuiButtonEmpty,
  EuiButtonIcon,
  EuiDescriptionList,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiPanel,
} from "@elastic/eui";
import { CatalogSubmitButton } from "../../catalog_submit_button";
import { ComplexQuestionEditorProps } from "src/questionnaires/types";
import { ComplexQuestionUserEditor } from "./user";
import { ComplexQuestionLocationEditor } from "./location";
import { ComplexQuestionDnsEntriesEditor } from "./dns_entries";
import { NetBoxDeviceEditor } from "./device";
import { ComplexQuestionNetworkEditor } from "./opsanio_network";
import { CustomerContactEditor } from "./customer_contact";
import { UpdateRecipientsEditor } from "./update_recipients";

export const generateGermanTitle = (key: string): string => {
  switch (key) {
    case "title":
      return "Title";
    case "job_title":
      return "Position";
    case "surname":
      return "Nachname";
    case "firstname":
      return "Vorname";
    case "sex":
      return "Geschlecht";
    case "email":
      return "E-Mail";
    case "phone":
      return "Telefonnummer";
    case "name":
      return "Name";
    case "street_name":
      return "Straßennamen";
    case "street_number":
      return "Straßen Nr.";
    case "zip":
      return "PLZ";
    case "city":
      return "Stadt";
    case "country":
      return "Land";
    case "employees_location":
      return "Mitarbeiter am Standort";
    case "hardware":
      return "Hardware";
    case "security":
      return "Cyber Security";
    case "services":
      return "Services";
    case "network":
      return "Netzwerk";
    case "server":
      return "Server";
    case "database":
      return "Database";
    case "cloud_computing":
      return "Cloud-Computing";
    case "programming_language":
      return "Programmiersprache";
    case "framework":
      return "Framework";
    case "storage":
      return "Storage";
    case "ap":
      return "Access Point";
    case "fw":
      return "Firewall";
    case "backup":
      return "Backup";
    case "archiv":
      return "Archiv";
    default:
      return key;
  }
};
export const ComplexQuestionEditor = ({
  question,
  setValue,
  invalid,
}: ComplexQuestionEditorProps): JSX.Element => {
  const [subCatalog, setSubCatalog] = useState<Item>();
  const ctx = useAnketa();
  //   let locale = ctx.i18nContext.language
  const [isModalVisible, setIsModalVisible] = useState(false);
  const closeModal = useCallback((): void => {
    setIsModalVisible(false);
  }, []);
  const saveModal = useCallback((): void => {
    if (!subCatalog) {
      return;
    }
    if (subCatalog.children.size > 0) {
      const rawObject = {} as any;
      subCatalog.children.forAny((item: Hierarchical) => {
        if (isQuestion(item)) {
          // Keep falsy-but-real answers (0, false); only skip unanswered ones.
          const raw = item.fact?.raw;
          if (raw !== undefined && raw !== null && raw !== "") {
            rawObject[item.id] = raw;
          }
        }
      });
      const complexVal = ComplexFactory.new(question.complexType, rawObject);
      setValue(complexVal);
    }
    setIsModalVisible(false);
  }, [subCatalog, question.complexType, setValue]);

  useEffect((): void => {
    // Build the complex type's own form and prefill it with the currently
    // stored value so editing shows the existing data.
    const form = question.getForm();
    const raw = question.fact?.raw as any;
    if (raw && typeof raw === "object") {
      form.children.forAny((item: Hierarchical) => {
        if (isQuestion(item) && raw[item.id] !== undefined) {
          (item as any).setFactValue(raw[item.id]);
        }
      });
    }
    setSubCatalog(form);
  }, [question]);

  const getModalEditor = useCallback((): JSX.Element => {
    return (
      <EuiModal onClose={closeModal} initialFocus="[name=popswitch]">
        <EuiModalHeader>
          <EuiModalHeaderTitle>
            <h1>{ctx.i18nContext.render(question.title)}</h1>
          </EuiModalHeaderTitle>
        </EuiModalHeader>
        <EuiModalBody>
          {/* Render the complex type's own form (getForm), not the whole
              questionnaire catalog. */}
          {subCatalog ? <>{ctx.childrenForItem(subCatalog)}</> : null}
        </EuiModalBody>

        <EuiModalFooter>
          <EuiButtonEmpty onClick={closeModal}>
            {ctx.i18nContext.render(ctx.messages.get(MessageCollection.cancel))}
          </EuiButtonEmpty>
          <CatalogSubmitButton onClick={saveModal}></CatalogSubmitButton>
        </EuiModalFooter>
      </EuiModal>
    );
  }, [ctx, closeModal, question.title, saveModal, subCatalog]);

  let control: JSX.Element;

  if (question.complexType === "KeycloakUserSales") {
    return (
      <ComplexQuestionUserEditor
        question={question}
        setValue={setValue}
        invalid={invalid}
        type="sales"
      />
    );
  }

  if (question.complexType === "OpsanioNetworkSelection") {
    return (
      <ComplexQuestionNetworkEditor
        question={question}
        setValue={setValue}
        invalid={invalid}
      />
    );
  }

  if (question.complexType === "KeycloakUserTechnical") {
    return (
      <ComplexQuestionUserEditor
        question={question}
        setValue={setValue}
        invalid={invalid}
        type="technical"
      />
    );
  }

  // Used exclusively by the General > Customer Contacts >
  // "Update notification recipients" field, which is a multi-value list of
  // e-mail addresses rather than a single contact.
  if (question.complexType === "KeycloakUserCustomer") {
    return (
      <UpdateRecipientsEditor
        question={question}
        setValue={setValue}
        invalid={invalid}
      />
    );
  }
  if (question.complexType === "ComplexLocation") {
    return (
      <ComplexQuestionLocationEditor
        question={question}
        setValue={setValue}
        invalid={invalid}
        type="multiple"
      />
    );
  }
  if (question.complexType === "OpsanioDnsEntries") {
    return (
      <ComplexQuestionDnsEntriesEditor
        question={question}
        setValue={setValue}
        invalid={invalid}
      />
    );
  }
  if (question.complexType === "CustomerContact") {
    return (
      <CustomerContactEditor
        question={question}
        setValue={setValue}
        invalid={invalid}
      />
    );
  }
  if (question.complexType === "NetboxDevices") {
    return (
      <NetBoxDeviceEditor
        question={question}
        setValue={setValue}
        invalid={invalid}
        type="server"
      />
    );
  }
  if (question.complexType === "NetboxSwitches") {
    return (
      <NetBoxDeviceEditor
        question={question}
        setValue={setValue}
        invalid={invalid}
        type="switch"
      />
    );
  }
  if (question.complexType === "NetboxDacs") {
    return (
      <NetBoxDeviceEditor
        question={question}
        setValue={setValue}
        invalid={invalid}
        type="dac"
      />
    );
  }
  if (question.complexType === "NetboxProbes") {
    return (
      <NetBoxDeviceEditor
        question={question}
        setValue={setValue}
        invalid={invalid}
        type="probe"
      />
    );
  }
  if (question.complexType === "Link") {
    if (question.fact.raw) {
      control = (
        <span>
          <a href={(question.fact.raw as Link).url}>
            {(question.fact.raw as Link).description}
          </a>
        </span>
      );
    } else {
      control = <span>{ctx.i18nContext.render(question.placeholder)}</span>;
    }
  } else {
    if (Object.entries(question.fact.raw ?? {}).length !== 0) {
      control = (
        <EuiPanel>
          {Object.entries(question.fact.raw ?? {})
            .filter(([_, value]) => value)
            .map(([key, value]) => {
              if (key.startsWith("_")) {
                return null;
              }
              return (
                <EuiDescriptionList
                  listItems={[
                    {
                      title: ctx.i18nContext
                        .render(generateGermanTitle(key))
                        .replaceAll("_", " "),
                      description: ctx.i18nContext.render(value),
                    },
                  ]}
                  compressed
                  type="column"
                  key={key}
                  style={{ width: "300px" }}
                />
              );
            })}
        </EuiPanel>
      );
    } else {
      control = <span>{ctx.i18nContext.render(question.placeholder)}</span>;
    }
  }

  let modal = <></>;
  if (isModalVisible) {
    modal = getModalEditor();
  }

  return (
    <>
      <EuiButtonIcon
        iconType="documentEdit"
        display="base"
        color="text"
        aria-label={ctx.i18nContext.render(
          ctx.messages.get(MessageCollection.edit),
        )}
        onClick={() => {
          setIsModalVisible(true);
        }}
        style={{ float: "right" }}
      />
      {modal}
      {control}
    </>
  );
};
